import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useScrollBehavior } from './useScrollBehavior.js';
import './info_module.css';

const InfoModule = ({ renderAnimatedLettersByWord }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  useScrollBehavior();

  const infoItems = [
    {
      title: 'Responsive Design',
      subtitle: 'Optimized for All Devices',
      body:
        "In today's mobile-driven landscape, your site needs to perform cleanly across every screen size. We build layouts that stay clear, usable, and visually consistent on desktop, tablet, and mobile."
    },
    {
      title: 'Digital Strategy',
      subtitle: 'Strategic Insights and Smarter Direction',
      body:
        'Strong websites are not built on visuals alone. We shape structure, messaging, and page flow around business goals so your digital presence supports visibility, engagement, and measurable growth.'
    },
    {
      title: 'E-commerce Websites',
      subtitle: 'Seamless Online Sales',
      body:
        'A good e-commerce experience needs more than products on a page. We help create smoother shopping paths, clearer product presentation, and a stronger user journey that supports trust and conversion.'
    },
    {
      title: 'Brand Identity',
      subtitle: 'Clear, Memorable Positioning',
      body:
        'Brand identity should feel cohesive, deliberate, and recognizable. We help shape the visual and structural language that makes your business feel more consistent, credible, and distinct.'
    }
  ];

  const container = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        staggerChildren: 0.12
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: 'easeOut' }
    }
  };

  return (
    <div id="infomod_wrap" ref={ref}>
      <motion.div
        id="infomod_intro"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={container}
      >
        <motion.div id="infomod_kicker" variants={item}>
          Structure • Clarity • Growth • Better User Experience
        </motion.div>

        <motion.h1 id="infomod_h1" variants={item}>
          {renderAnimatedLettersByWord(
            'Empowering your digital journey with stronger systems and clearer design.',
            inView
          )}
        </motion.h1>

        <motion.p id="infomod_p" variants={item}>
          Collaborate with a team focused on building digital experiences that do more than exist.
          From responsiveness and strategy to e-commerce structure and brand clarity, every piece
          should help your business perform more effectively online.
        </motion.p>
      </motion.div>

      <motion.div
        id="infomod_grid"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={container}
      >
        {infoItems.map((itemData, index) => (
          <motion.div className="infomod_card" variants={item} key={index}>
            <div className="infomod_cardTopLine"></div>
            <div className="infomod_cardTitle">{itemData.title}</div>
            <div className="infomod_cardSubtitle">{itemData.subtitle}</div>
            <div className="infomod_cardBody">{itemData.body}</div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default InfoModule;